import { Stack, Title, Group, Text, Badge, Skeleton, Card, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { formatBillingDate } from '@/shared/utils/date';
import { useUsage } from '../../hooks/useUsage';
import { useBillingPlan } from '../../hooks/useBilling';
import { useAccountPermissions } from '../../useAccountPermissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UsageSummaryCards } from './UsageSummaryCards';
import { TransferHistoryChart } from './TransferHistoryChart';
import { TrialUpgradeBanner } from './TrialUpgradeBanner';

export function UsagePage() {
  const { usage, isLoading } = useUsage();
  const { billingPlan, isLoading: isPlanLoading, isError: isPlanError } = useBillingPlan();
  const { isTrial } = useAccountPermissions();
  const { isSubaccount, selectedCustomer } = useAuth();

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Usage</Title>
        <Skeleton height={100} radius="md" />
        <Group grow>
          <Skeleton height={120} radius="md" />
          <Skeleton height={120} radius="md" />
        </Group>
        <Skeleton height={300} radius="md" />
      </Stack>
    );
  }

  if (!usage) {
    return (
      <Stack gap="lg">
        <Title order={2}>Usage</Title>
        <Text c="dimmed">No usage data available.</Text>
      </Stack>
    );
  }

  const planData = billingPlan?.billingPlan;
  const accountLabel = selectedCustomer?.cfg.label ?? 'Account';
  const nextBilling = billingPlan ? formatBillingDate(billingPlan.expires) : null;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>Usage</Title>
          {isSubaccount && (
            <Text size="sm" c="dimmed" mt={2}>
              Showing usage for: {accountLabel}
            </Text>
          )}
        </div>
      </Group>

      {isTrial && planData && (
        <TrialUpgradeBanner planName={planData.publicLabel || planData.name} />
      )}

      {isPlanError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
          Failed to load plan information.
        </Alert>
      )}

      {!isPlanLoading && planData && (
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between">
            <Group gap="sm">
              <Text size="sm" c="dimmed">Plan:</Text>
              <Badge variant="light" size="lg">
                {planData.publicLabel || planData.name}
              </Badge>
            </Group>
            {nextBilling && (
              <Group gap="sm">
                <Text size="sm" c="dimmed">Next billing:</Text>
                <Text size="sm" fw={500}>{nextBilling}</Text>
              </Group>
            )}
          </Group>
        </Card>
      )}

      <UsageSummaryCards
        storageBytesUsed={usage.storageBytesUsed}
        storageBytesLimit={usage.storageBytesLimit}
        transferBytesUsed={usage.transferBytesUsed}
        transferBytesLimit={usage.transferBytesLimit}
      />

      <TransferHistoryChart monthlyTransfer={usage.monthlyTransfer} />
    </Stack>
  );
}
